import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EmailOtpType } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { syncHospitalIdFromMetadata } from '@/lib/hospitalContext';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

function decodeAuthError(description: string | null): string | null {
  if (!description) return null;
  try {
    return decodeURIComponent(description.replace(/\+/g, ' '));
  } catch {
    return description;
  }
}

async function markProfileVerified(userId: string) {
  const { error } = await supabase
    .from('profiles')
    .update({ status: 'verified' })
    .eq('id', userId);
  if (error) {
    console.warn('Could not update profile verification status:', error.message);
  }
}

export function ConfirmEmail() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const finishSuccess = async (userId: string, email?: string | null) => {
      await syncHospitalIdFromMetadata();
      await markProfileVerified(userId);
      await supabase.auth.signOut();
      window.history.replaceState(null, '', window.location.pathname);

      if (cancelled) return;

      setStatus('success');
      setMessage('Your email has been confirmed successfully! You can now log in.');
      toast({
        title: 'Email Confirmed',
        description: email
          ? `${email} is verified. You can now log in.`
          : 'Your email has been confirmed. You can now log in.',
      });

      setTimeout(() => {
        navigate('/login', {
          replace: true,
          state: { message: 'Email confirmed successfully. Please log in.' },
        });
      }, 2000);
    };

    const confirmEmail = async () => {
      try {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);

        const error = hashParams.get('error') || queryParams.get('error');
        const errorDescription = decodeAuthError(
          hashParams.get('error_description') || queryParams.get('error_description')
        );
        const errorCode = hashParams.get('error_code') || queryParams.get('error_code');

        if (error) {
          console.error('Email confirmation error:', { error, errorCode, errorDescription });
          if (cancelled) return;
          setStatus('error');
          if (errorCode === 'otp_expired') {
            setMessage(
              'This confirmation link has expired. Please register again or contact support for a new link.'
            );
          } else if (error === 'access_denied' || errorDescription?.toLowerCase().includes('redirect')) {
            setMessage(
              'Confirmation link could not reach the app (redirect URL not allowed). Please contact support — your account may still need manual verification.'
            );
          } else {
            setMessage(
              errorDescription ||
                'Email confirmation failed. The link may be invalid or expired.'
            );
          }
          return;
        }

        const type =
          (queryParams.get('type') || hashParams.get('type')) as EmailOtpType | null;
        const tokenHash = queryParams.get('token_hash');
        const accessToken =
          hashParams.get('access_token') || queryParams.get('access_token');
        const refreshToken =
          hashParams.get('refresh_token') || queryParams.get('refresh_token');

        // PKCE / newer Supabase email links: ?token_hash=...&type=signup
        if (tokenHash && type) {
          const { data, error: otpError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type,
          });

          if (otpError) {
            if (cancelled) return;
            setStatus('error');
            setMessage(otpError.message || 'Failed to verify email confirmation.');
            return;
          }

          if (data?.user) {
            await finishSuccess(data.user.id, data.user.email);
            return;
          }
        }

        // Legacy implicit flow: #access_token=...&refresh_token=...&type=signup
        if (accessToken && refreshToken) {
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            if (cancelled) return;
            setStatus('error');
            setMessage(sessionError.message || 'Failed to verify email confirmation.');
            return;
          }

          if (data?.user) {
            await finishSuccess(data.user.id, data.user.email);
            return;
          }

          if (cancelled) return;
          setStatus('error');
          setMessage('Email confirmation failed. No user data received.');
          return;
        }

        if (type === 'recovery') {
          navigate('/reset-password', { replace: true });
          return;
        }

        if (type === 'invite') {
          if (cancelled) return;
          setStatus('error');
          setMessage('This link is for invitation, not email confirmation.');
          return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email_confirmed_at) {
          await markProfileVerified(user.id);
          if (cancelled) return;
          setStatus('success');
          setMessage('Your email is already confirmed. You can log in.');
          setTimeout(() => navigate('/login', { replace: true }), 2000);
          return;
        }

        if (cancelled) return;
        setStatus('error');
        setMessage(
          'Invalid confirmation link. Please check your email for the latest link or contact support.'
        );
      } catch (err: unknown) {
        console.error('Email confirmation error:', err);
        if (cancelled) return;
        setStatus('error');
        setMessage(
          err instanceof Error
            ? err.message
            : 'An unexpected error occurred during email confirmation.'
        );
      }
    };

    confirmEmail();

    return () => {
      cancelled = true;
    };
  }, [navigate, toast]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {status === 'loading' && <Loader2 className="h-5 w-5 animate-spin" />}
            {status === 'success' && <CheckCircle className="h-5 w-5 text-green-600" />}
            {status === 'error' && <XCircle className="h-5 w-5 text-red-600" />}
            Email Confirmation
          </CardTitle>
          <CardDescription>
            {status === 'loading' && 'Confirming your email address...'}
            {status === 'success' && 'Confirmation successful'}
            {status === 'error' && 'Confirmation failed'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {message || 'Processing your email confirmation...'}
          </p>

          {status === 'success' && (
            <div className="space-y-2">
              <p className="text-sm text-green-600">
                Redirecting you to the login page...
              </p>
              <Button onClick={() => navigate('/login')} className="w-full">
                Go to Login
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full"
              >
                Go to Login
              </Button>
              <Button
                onClick={() => window.location.reload()}
                variant="ghost"
                className="w-full"
              >
                Try Again
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
